/**
 * Hybrid Feature Flag Provider
 * Uses runtime config with default fallbacks
 * Priority: Runtime Config > Defaults
 */

import type {
  FeatureFlagProvider,
  FeatureFlags,
} from "../feature-flags.interface";
import { createLogger } from "@/services/logger/logger.service";

const logger = createLogger("HybridProvider");
import { RuntimeConfigFeatureFlagProvider } from "./runtime-config.provider";

export class HybridFeatureFlagProvider implements FeatureFlagProvider {
  private runtimeProvider: RuntimeConfigFeatureFlagProvider;

  constructor() {
    this.runtimeProvider = new RuntimeConfigFeatureFlagProvider();
  }

  get<K extends keyof FeatureFlags>(key: K): FeatureFlags[K] {
    // Use runtime config which already includes defaults as fallback
    return this.runtimeProvider.get(key);
  }

  set<K extends keyof FeatureFlags>(key: K, _value: FeatureFlags[K]): void {
    // Read-only in production
    logger.warn(
      `Cannot set feature flag ${key} - use runtime config to override`,
    );
  }

  getAll(): FeatureFlags {
    // Return runtime flags (which include defaults)
    return this.runtimeProvider.getAll();
  }

  reset(): void {
    // Reset to defaults
    this.runtimeProvider.reset();
  }

  isEnabled(key: keyof FeatureFlags): boolean {
    const value = this.get(key);
    return typeof value === "boolean" ? value : false;
  }

  /**
   * No-op refresh (no API to refresh from)
   */
  async refresh(): Promise<void> {
    // No-op - runtime config doesn't need refreshing
    return Promise.resolve();
  }
}
